import { puzzles } from '@/game/puzzles';
import type { PuzzleData } from '@/types/puzzle';
import { getPuzzleGrid } from '@/types/puzzle';
import { useCameraZoom } from '@/hooks/useCameraZoom';
import type { Direction, GameState, Position } from '@/types/game';
import { getWorldForLevel } from '@/config/worlds';
import { motion, AnimatePresence } from 'motion/react';
import React, { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Character from './elements/Character';
import Coin from './elements/Coin';
import EnemyRenderer from './EnemyRenderer';
const ProceduralParticleSystem = React.lazy(() => import('./ProceduralParticleSystem'));
import { useShop } from '@/contexts/ShopContext';
import type { CharacterConfig } from './elements/Character3D';
import { PROPS_CATALOG } from '@/types/props';
import { getCommentarySystem } from '@/game/commentarySystem';

const CHARACTER_TAP_LINES = [
  'Hey! I am trying to focus here!',
  'Stop poking me!',
  'Do I look like a button to you?',
  'That tickles!',
  'Oi! Hands off!',
  'I am NOT a stress ball!',
  'Boop!',
  'What? I am working!',
  'Can you not?',
  'One more poke and I quit!',
  'Yes, I am adorable. Now let me slide.',
  'Excuse me, personal space?',
  'I am trying to get HOME here!',
  'You tap, I wobble. Happy now?',
  'Plot twist: I tap YOU back.',
  'Please file a complaint with management.',
];
import { getPropEffectsManager } from '@/game/propEffects';

// Character configurations - should match CharacterShopSection and HomeCharacter
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

const CHARACTER_CONFIGS: Record<string, DeepPartial<CharacterConfig>> = {
  default_hero: {}, // Uses all defaults
  tiny_blue: {
    sizes: { characterSize: 0.5, cubeSize: 0.95 },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #2968a3 100%)',
        back: 'linear-gradient(135deg, #357abd 0%, #2968a3 50%, #1e5189 100%)',
        top: 'linear-gradient(135deg, #5ba0f2 0%, #4a90e2 50%, #357abd 100%)',
        bottom: 'linear-gradient(135deg, #2968a3 0%, #1e5189 50%, #143a5f 100%)',
        left: 'linear-gradient(135deg, #4080d0 0%, #3570c0 50%, #2a60b0 100%)',
        right: 'linear-gradient(135deg, #4585d5 0%, #3a75c5 50%, #2f65b5 100%)',
      },
    },
    face: { eyes: { size: '35%' } },
  },
  chonky_green: {
    sizes: { characterSize: 0.95, cubeSize: 0.85, shadow: { width: 0.9, height: 0.25, offsetY: 0.12 } },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)',
        back: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 50%, #1b5e20 100%)',
        top: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 50%, #43a047 100%)',
        bottom: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 50%, #0d3f15 100%)',
        left: 'linear-gradient(135deg, #43a047 0%, #3b9040 50%, #338037 100%)',
        right: 'linear-gradient(135deg, #48a84c 0%, #409843 50%, #38883b 100%)',
      },
    },
    face: { eyes: { size: '15%', gap: '35%' }, mouth: { width: '40%' } },
  },
  sleek_purple: {
    sizes: { characterSize: 0.75, cubeSize: 0.98 },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #9c27b0 0%, #8e24aa 50%, #7b1fa2 100%)',
        back: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)',
        top: 'linear-gradient(135deg, #ab47bc 0%, #9c27b0 50%, #8e24aa 100%)',
        bottom: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 50%, #2a0a5c 100%)',
        left: 'linear-gradient(135deg, #8e24aa 0%, #7e22a0 50%, #6e1f96 100%)',
        right: 'linear-gradient(135deg, #9125ac 0%, #8123a2 50%, #712098 100%)',
      },
      effects: { shadow: 'radial-gradient(ellipse, rgba(156, 39, 176, 0.4) 0%, transparent 70%)' },
    },
    face: {
      eyes: { size: '22%', shine: { size: '40%', position: { top: '15%', left: '20%' } } },
      mouth: { width: '30%', heights: { neutral: '12%', happy: '20%', shocked: '25%' } },
    },
  },
  golden_glow: {
    sizes: { characterSize: 0.8, cubeSize: 0.9 },
    colors: {
      faces: {
        front: 'linear-gradient(135deg, #ffc107 0%, #ffb300 50%, #ffa000 100%)',
        back: 'linear-gradient(135deg, #ffa000 0%, #ff8f00 50%, #ff6f00 100%)',
        top: 'linear-gradient(135deg, #ffca28 0%, #ffc107 50%, #ffb300 100%)',
        bottom: 'linear-gradient(135deg, #ff8f00 0%, #ff6f00 50%, #e65100 100%)',
        left: 'linear-gradient(135deg, #ffb300 0%, #ffa300 50%, #ff9300 100%)',
        right: 'linear-gradient(135deg, #ffb800 0%, #ffa800 50%, #ff9800 100%)',
      },
    },
    backFace: { symbol: '*', sizeScale: 0.4, opacity: 0.6 },
  },
};

interface GameBoardProps {
  gameState: GameState;
  onCellClick?: (position: Position) => void;
  hintPath?: Position[] | null;
  /** Set of "col,row" keys for hint-path tiles near enemies (hazard zones) */
  hintPathHazards?: Set<string>;
  onMovementLockChange?: (isLocked: boolean) => void;
  onExitAnimationComplete?: () => void;
  restartCounter?: number;
}

interface PlayerAnimationState {
  isMoving: boolean;
  direction: 'up' | 'down' | 'left' | 'right' | null;
  hasCollided: boolean;
  previousPosition: Position;
}

type CharacterGameState = 'playing' | 'won' | 'lost' | 'entering' | 'exiting';

interface GameCell {
  type: 'empty' | 'rock' | 'lava' | 'stop' | 'teleporter' | 'start' | 'end' | 'powerup' | 'obstacle';
  position: Position;
  data?: any;
  zIndex?: number;
}

// Particle component for effects - memoized to prevent re-renders from parent
const Particle = memo<{
  x: number;
  y: number;
  color: string;
  size: number;
  duration: number;
  delay: number;
  driftX1: number;
  driftX2: number;
}>(({ x, y, color, size, duration, delay, driftX1, driftX2 }) => (
  <motion.div
    style={{
      position: 'absolute',
      left: x,
      top: y,
      width: size,
      height: size,
      background: color,
      borderRadius: '50%',
      pointerEvents: 'none',
    }}
    initial={{ opacity: 0, scale: 0 }}
    animate={{
      opacity: [0, 1, 0],
      scale: [0, 1, 0],
      y: [0, -20, -40],
      x: [0, driftX1, driftX2],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeOut',
    }}
  />
));

// Portal particle for start/end - memoized
const PortalParticle = memo<{
  angle: number;
  radius: number;
  color: string;
  cellSize: number;
  delay: number;
  elevation?: number;
}>(({ angle, radius, color, cellSize, delay, elevation = 0 }) => {
  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: cellSize / 2,
        top: cellSize / 2,
        width: 4,
        height: 4,
        background: color,
        borderRadius: '50%',
        boxShadow: `0 0 6px ${color}`,
        pointerEvents: 'none',
        transform: `translateY(-${elevation}px)`,
      }}
      animate={{
        x: [0, x, x * 2, 0],
        y: [0, y, y * 2, 0],
        opacity: [0, 1, 1, 0],
        scale: [0, 1, 0.5, 0],
      }}
      transition={{
        duration: 3,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
});

// Get obstacle heights
export const OBSTACLE_HEIGHTS = {
  lava: 0.15,
  stop: 0.15,
  ice: 0.15,
  teleporter: 0.12,
  rock: 0.15, // Match ice and lava height
  empty: 0,
  start: 0.12, // Raised platform
  end: 0.12, // Raised platform
  powerup: 0,
  obstacle: 0.15,
};

/**
 * Compute which directions a player can approach the exit tile from.
 * A direction is valid if the tile immediately BEYOND the exit (in that slide
 * direction) is a wall, obstacle, or grid edge -- meaning the player would
 * stop ON the exit when sliding from that direction.
 */
function getExitApproachDirections(
  exitPos: Position,
  grid: string[],
  rocks: Array<[number, number, string]>,
): Direction[] {
  const directions: Direction[] = [];
  const gridRows = grid.length;
  const gridCols = grid[0]?.length || 0;
  const [ex, ey] = exitPos;

  const isBlocked = (x: number, y: number) => {
    if (x < 0 || x >= gridCols || y < 0 || y >= gridRows) return true;
    const c = grid[y]?.[x]?.toLowerCase();
    if (c === 'x' || c === 'l') return true;
    return rocks.some(r => r[0] === x && r[1] === y && r[2] === 'rock');
  };

  // For each cardinal direction, check two conditions:
  // 1. Beyond the exit in that direction is blocked (so the player stops ON exit)
  // 2. The approach side (opposite) has at least one open tile (player can slide from there)
  const checks: { dir: Direction; dx: number; dy: number }[] = [
    { dir: 'right', dx: 1, dy: 0 },
    { dir: 'left', dx: -1, dy: 0 },
    { dir: 'down', dx: 0, dy: 1 },
    { dir: 'up', dx: 0, dy: -1 },
  ];

  for (const { dir, dx, dy } of checks) {
    // Beyond the exit must be blocked
    if (!isBlocked(ex + dx, ey + dy)) continue;

    // Approach side must have at least one open tile the player could slide from
    const ax = ex - dx;
    const ay = ey - dy;
    if (ax < 0 || ax >= gridCols || ay < 0 || ay >= gridRows) continue;
    const approachCell = grid[ay]?.[ax]?.toLowerCase();
    if (approachCell === 'x' || approachCell === 'l') continue;
    if (rocks.some(r => r[0] === ax && r[1] === ay && r[2] === 'rock')) continue;

    directions.push(dir);
  }

  return directions;
}

// Arrow rotation for each approach direction (the arrow points in the
// direction the player should slide, so FROM the opposite side).
const EXIT_ARROW_ROTATION: Record<Direction, number> = {
  right: 0,    // Arrow points right (enter from left)
  down: 90,    // Arrow points down (enter from top)
  left: 180,   // Arrow points left (enter from right)
  up: 270,     // Arrow points up (enter from bottom)
};

// Position offset for each direction (one full cell back from the exit tile)
const EXIT_ARROW_OFFSET: Record<Direction, { x: number; y: number }> = {
  right: { x: -1, y: 0 },   // Cell to the left of exit
  left: { x: 1, y: 0 },     // Cell to the right of exit
  down: { x: 0, y: -1 },    // Cell above exit
  up: { x: 0, y: 1 },       // Cell below exit
};

// Pulsing motion along the approach direction
const EXIT_ARROW_PULSE: Record<Direction, { x: number[]; y: number[] }> = {
  right: { x: [-3, 3, -3], y: [0, 0, 0] },
  left: { x: [3, -3, 3], y: [0, 0, 0] },
  down: { x: [0, 0, 0], y: [-3, 3, -3] },
  up: { x: [0, 0, 0], y: [3, -3, 3] },
};

// Front-facing 3D tile component
const Tile3D: React.FC<{
  type: GameCell['type'];
  position: Position;
  cellSize: number;
  isPath?: boolean;
  zIndex?: number;
  exitApproachDirections?: Direction[];
}> = memo(({ type, position, cellSize, isPath, zIndex, exitApproachDirections }) => {
  const depth = cellSize * 0.1; // 10% of cell size for depth

  const getColors = () => {
    switch (type) {
      case 'start':
        return {
          top: '#81C784',
          front: '#66BB6A',
          side: '#4CAF50',
          glow: 'rgba(129, 199, 132, 0.5)',
        };
      case 'end':
        return {
          top: '#64B5F6',
          front: '#42A5F5',
          side: '#2196F3',
          glow: 'rgba(100, 181, 246, 0.5)',
        };
      case 'rock':
        return {
          top: '#9E9E9E',
          front: '#757575',
          side: '#616161',
        };
      case 'lava':
        return {
          top: '#FF7043',
          front: '#FF5722',
          side: '#E64A19',
          glow: 'rgba(255, 87, 34, 0.6)',
        };
      case 'stop':
        return {
          top: '#4DD0E1',
          front: '#00BCD4',
          side: '#0097A7',
          glow: 'rgba(0, 188, 212, 0.4)',
        };
      case 'teleporter':
        return {
          top: '#CE93D8',
          front: '#AB47BC',
          side: '#8E24AA',
          glow: 'rgba(156, 39, 176, 0.5)',
        };
      default:
        if (isPath) {
          return {
            top: '#FFF8DC',
            front: '#FAEBD7',
            side: '#DEB887',
          };
        }
        return {
          top: '#F5F5F5',
          front: '#E0E0E0',
          side: '#BDBDBD',
        };
    }
  };

  const colors = getColors();
  const isObstacle = type === 'lava' || type === 'stop' || type === 'teleporter';
  const isSpecial = type === 'start' || type === 'end';
  const obstacleHeight = cellSize * OBSTACLE_HEIGHTS[type];
  const wallHeight = cellSize * OBSTACLE_HEIGHTS.rock;

  // Generate particles for obstacles
  const particles = useMemo(() => {
    if (!isObstacle) return [];
    const count = type === 'lava' ? 8 : type === 'teleporter' ? 6 : 6;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * cellSize * 0.6 + cellSize * 0.2,
      y: Math.random() * cellSize * 0.6 + cellSize * 0.2,
      color:
        type === 'lava'
          ? `rgba(255, ${100 + Math.random() * 100}, 0, 0.8)`
          : type === 'teleporter'
            ? `rgba(${180 + Math.random() * 75}, ${50 + Math.random() * 80}, ${200 + Math.random() * 55}, 0.8)`
            : `rgba(${150 + Math.random() * 105}, ${200 + Math.random() * 55}, 255, 0.8)`,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 2 + 1,
      delay: Math.random() * 2,
      driftX1: (Math.random() - 0.5) * 20,
      driftX2: (Math.random() - 0.5) * 40,
    }));
  }, [isObstacle, type, cellSize]);

  // Generate portal particles for start/end
  const portalParticles = useMemo(() => {
    if (!isSpecial) return [];
    const count = 12;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      angle: (i / count) * Math.PI * 2,
      radius: cellSize * 0.3,
      color: type === 'start' ? '#4CAF50' : '#2196F3',
      delay: i * 0.1,
    }));
  }, [isSpecial, type, cellSize]);

  return (
    <motion.div
      style={{
        position: 'absolute',
        left: position[0] * cellSize,
        top: position[1] * cellSize,
        width: cellSize,
        height: cellSize,
        zIndex: zIndex !== undefined ? zIndex : 100, // Use provided z-index or default
      }}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{
        delay: (position[0] + position[1]) * 0.02,
        type: 'spring',
        stiffness: 260,
        damping: 20,
      }}
    >
      {/* Base tile - simplified approach */}
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* Main surface */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: colors.top,
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: `
              -${depth / 2}px ${depth}px 0 ${colors.side},
              -${depth / 2}px ${depth}px 0 1px rgba(0,0,0,0.2),
              0 ${depth}px 0 ${colors.front},
              0 ${depth}px 0 1px rgba(0,0,0,0.1),
              inset 0 -2px 4px rgba(0,0,0,0.1)
            `,
          }}
        />

        {/* Special tile effects for start/end - 3D portal */}
        {isSpecial && (
          <>
            {/* Portal base platform - raised square */}
            <motion.div
              style={{
                position: 'absolute',
                width: '90%',
                height: '90%',
                left: '5%',
                top: '5%',
                borderRadius: '15%',
                background: `linear-gradient(135deg, ${
                  type === 'start' ? '#4CAF50' : '#2196F3'
                } 0%, ${type === 'start' ? '#388E3C' : '#1976D2'} 50%, ${
                  type === 'start' ? '#2E7D32' : '#1565C0'
                } 100%)`,
                transform: `translateY(-${obstacleHeight}px)`,
                boxShadow: `
                  0 ${obstacleHeight}px 0 ${type === 'start' ? '#2E7D32' : '#1565C0'},
                  0 ${obstacleHeight}px 0 2px rgba(0,0,0,0.3),
                  0 ${depth}px ${depth * 2}px rgba(0,0,0,0.2),
                  0 0 40px ${colors.glow},
                  inset 0 2px 4px rgba(255,255,255,0.3),
                  inset 0 -2px 4px rgba(0,0,0,0.2)
                `,
                overflow: 'hidden',
              }}
            >
              {/* Add portal symbol */}
              <div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: cellSize * 0.4,
                  fontWeight: 'bold',
                  color: 'rgba(255, 255, 255, 0.9)',
                  textShadow: `0 0 10px ${colors.glow}`,
                  marginLeft: 3,
                }}
              >
                {type === 'start' ? 'S' : 'E'}
              </div>
            </motion.div>

            {/* Animated portal energy rings - square */}
            {/* {[0, 1, 2].map(i => (
              <motion.div
                key={i}
                style={{
                  position: 'absolute',
                  width: `${80 - i * 15}%`,
                  height: `${80 - i * 20}%`,
                  left: `${10 + i * 7.5}%`,
                  top: `${10 + i * 0.5}%`,
                  borderRadius: '12%',
                  border: `2px solid ${type === 'start' ? '#81C784' : '#64B5F6'}`,
                  opacity: 0.6,
                  pointerEvents: 'none',
                  transform: `translateY(-${obstacleHeight}px)`,
                }}
                animate={{
                  scale: [1, 1.15, 1],
                  opacity: [0.6, 0.2, 0.6],
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            ))} */}

            {/* Swirling energy effect */}
            {/* <motion.div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                transform: `translateY(-${obstacleHeight}px)`,
              }}
              animate={{
                rotate: type === 'start' ? 360 : -360,
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              {[0, 120, 240].map(angle => (
                <motion.div
                  key={angle}
                  style={{
                    position: 'absolute',
                    width: '15%',
                    height: '15%',
                    left: '42.5%',
                    top: '22.5%',
                    borderRadius: '50%',
                    background: colors.glow,
                    filter: 'blur(4px)',
                    transform: `rotate(${angle}deg) translateX(${cellSize * 0.3}px)`,
                  }}
                  animate={{
                    scale: [0.5, 1, 0.5],
                    opacity: [0.8, 0.3, 0.8],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </motion.div> */}

            {/* Central glowing core - square */}
            {/* <motion.div
              style={{
                position: 'absolute',
                width: '50%',
                height: '50%',
                left: '25%',
                top: '15%',
                borderRadius: '28%',
                background: `radial-gradient(circle, ${
                  type === 'start' ? '#81C784' : '#64B5F6'
                } 0%, ${colors.glow} 40%, transparent 70%)`,
                boxShadow: `
                  0 0 30px ${colors.glow},
                  inset 0 0 20px ${type === 'start' ? '#4CAF50' : '#2196F3'}
                `,
                pointerEvents: 'none',
                transform: `translateY(-${obstacleHeight}px)`,
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.8, 1, 0.8],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            /> */}

            {/* Portal particles */}
            {portalParticles.map(particle => (
              <PortalParticle
                key={particle.id}
                {...particle}
                cellSize={cellSize}
                elevation={obstacleHeight}
              />
            ))}

            {/* Vertical light beam */}
            {/* <motion.div
              style={{
                position: 'absolute',
                width: '2%',
                height: '300%',
                left: '49%',
                top: '-100%',
                background: `linear-gradient(to top, transparent, ${colors.glow}, transparent)`,
                pointerEvents: 'none',
                opacity: 0.5,
                transform: `translateY(-${obstacleHeight}px)`,
              }}
              animate={{
                opacity: [0.3, 0.7, 0.3],
                scaleY: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            /> */}

            {/* Ground reflection */}
            <div
              style={{
                position: 'absolute',
                width: '80%',
                height: '20%',
                left: '10%',
                bottom: `-${depth * 2}px`,
                background: `radial-gradient(ellipse, ${colors.glow} 0%, transparent 60%)`,
                borderRadius: '50%',
                opacity: 0.3,
                filter: 'blur(4px)',
              }}
            />

            {/* Exit approach direction arrows */}
            {type === 'end' && exitApproachDirections && exitApproachDirections.length > 0 && (
              exitApproachDirections.map(dir => {
                const offset = EXIT_ARROW_OFFSET[dir];
                const pulse = EXIT_ARROW_PULSE[dir];
                const rotation = EXIT_ARROW_ROTATION[dir];
                const arrowSize = cellSize * 0.22;

                return (
                  <motion.div
                    key={`exit-arrow-${dir}`}
                    style={{
                      position: 'absolute',
                      left: `calc(50% + ${offset.x * cellSize}px)`,
                      top: `calc(50% + ${offset.y * cellSize}px)`,
                      width: 0,
                      height: 0,
                      transform: `translate(-50%, -50%) rotate(${rotation}deg) translateY(-${obstacleHeight}px)`,
                      borderTop: `${arrowSize * 0.6}px solid transparent`,
                      borderBottom: `${arrowSize * 0.6}px solid transparent`,
                      borderLeft: `${arrowSize}px solid rgba(100, 181, 246, 0.9)`,
                      filter: 'drop-shadow(0 0 4px rgba(100, 181, 246, 0.6))',
                      pointerEvents: 'none',
                      zIndex: 5,
                    }}
                    animate={{
                      x: pulse.x,
                      y: pulse.y,
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                );
              })
            )}
          </>
        )}
      </div>

      {/* Obstacles (raised blocks) */}
      {isObstacle && (
        <motion.div
          style={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            left: '10%',
            top: '10%',
          }}
          animate={
            type === 'lava'
              ? {
                  y: [-1, 0, -1],
                }
              : {}
          }
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: colors.top,
              border: `2px solid ${colors.side}`,
              transform: `translateY(-${obstacleHeight}px)`,
              boxShadow: `
                0 ${obstacleHeight}px 0 ${colors.front},
                0 ${obstacleHeight}px 0 2px ${colors.side},
                ${colors.glow ? `0 0 15px ${colors.glow}` : ''}
              `,
            }}
          />

          {/* Particles */}
          {particles.map(particle => (
            <Particle key={particle.id} {...particle} />
          ))}

          {/* Ice crystal shimmer effect (GPU-accelerated via transform) */}
          {type === 'stop' && (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                transform: `translateY(-${obstacleHeight}px)`,
              }}
            >
              <motion.div
                style={{
                  position: 'absolute',
                  width: '200%',
                  height: '200%',
                  top: '-50%',
                  left: '-50%',
                  background:
                    'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)',
                }}
                animate={{
                  x: ['-25%', '25%'],
                  y: ['-25%', '25%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              />
            </div>
          )}

          {/* Teleporter portal swirl */}
          {type === 'teleporter' && (
            <motion.div
              style={{
                position: 'absolute',
                width: '70%',
                height: '70%',
                left: '15%',
                top: '15%',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(206,147,216,0.6) 0%, rgba(156,39,176,0.3) 50%, transparent 70%)',
                transform: `translateY(-${obstacleHeight}px)`,
                boxShadow: '0 0 12px rgba(156,39,176,0.4)',
              }}
              animate={{
                rotate: [0, 360],
                scale: [0.9, 1.1, 0.9],
              }}
              transition={{
                rotate: { duration: 3, repeat: Infinity, ease: 'linear' },
                scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
              }}
            />
          )}
        </motion.div>
      )}

      {/* Walls (tall blocks) */}
      {type === 'rock' && (
        <div
          style={{
            position: 'absolute',
            width: '90%',
            height: '90%',
            left: '5%',
            top: '5%',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: `linear-gradient(to bottom, ${colors.top}, ${colors.front})`,
              border: '2px solid rgba(0,0,0,0.2)',
              transform: `translateY(-${wallHeight}px)`,
              boxShadow: `
                0 ${wallHeight}px 0 ${colors.side},
                0 ${wallHeight}px 0 2px rgba(0,0,0,0.3),
                0 4px 8px rgba(0,0,0,0.3)
              `,
            }}
          />
        </div>
      )}

      {/* Powerups */}
      {type === 'powerup' && (
        <motion.div
          style={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            left: '20%',
            top: '20%',
            transform: `translateY(-${cellSize * 0.3}px)`,
          }}
          animate={{
            y: [-5, 5, -5],
            rotateZ: [0, 360],
          }}
          transition={{
            y: {
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            },
            rotateZ: {
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            },
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'radial-gradient(circle, #FFD700, #FFA000)',
              borderRadius: '50%',
              border: '3px solid #FF8F00',
              boxShadow: '0 0 20px rgba(255, 215, 0, 0.8), 0 4px 8px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: `${cellSize * 0.3}px`,
              fontWeight: 'bold',
              color: '#FF6F00',
            }}
          >
            $
          </div>
        </motion.div>
      )}
    </motion.div>
  );
});

// Teleport effect overlay on source/destination tiles
const TeleportEffect: React.FC<{
  position: Position;
  cellSize: number;
  type: 'suckIn' | 'growOut';
}> = memo(({ position, cellSize, type }) => (
  <motion.div
    style={{
      position: 'absolute',
      left: position[0] * cellSize,
      top: position[1] * cellSize,
      width: cellSize,
      height: cellSize,
      zIndex: 2500,
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {/* Outer swirl ring */}
    <motion.div
      style={{
        position: 'absolute',
        width: '90%',
        height: '90%',
        borderRadius: '50%',
        border: '3px solid rgba(156, 39, 176, 0.8)',
        boxShadow: '0 0 25px rgba(156, 39, 176, 0.7), inset 0 0 15px rgba(206, 147, 216, 0.4)',
      }}
      initial={type === 'suckIn' ? { scale: 1.8, opacity: 0 } : { scale: 0, opacity: 0 }}
      animate={type === 'suckIn'
        ? { scale: 0, opacity: [0, 1, 1, 0], rotate: 540 }
        : { scale: 1.8, opacity: [0, 1, 1, 0], rotate: -540 }
      }
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
    />
    {/* Inner swirl ring */}
    <motion.div
      style={{
        position: 'absolute',
        width: '60%',
        height: '60%',
        borderRadius: '50%',
        border: '2px solid rgba(206, 147, 216, 0.6)',
      }}
      initial={type === 'suckIn' ? { scale: 1.4, opacity: 0 } : { scale: 0, opacity: 0 }}
      animate={type === 'suckIn'
        ? { scale: 0, opacity: [0, 0.8, 0], rotate: -360 }
        : { scale: 1.4, opacity: [0, 0.8, 0], rotate: 360 }
      }
      transition={{ duration: 0.5, ease: 'easeInOut', delay: 0.05 }}
    />
    {/* Core glow */}
    <motion.div
      style={{
        position: 'absolute',
        width: '50%',
        height: '50%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(206, 147, 216, 0.9) 0%, rgba(156, 39, 176, 0.5) 40%, transparent 70%)',
      }}
      initial={type === 'suckIn' ? { scale: 2.5, opacity: 0 } : { scale: 0, opacity: 0 }}
      animate={type === 'suckIn'
        ? { scale: 0, opacity: [0, 1, 0] }
        : { scale: 2.5, opacity: [0, 1, 0] }
      }
      transition={{ duration: 0.6, ease: 'easeInOut' }}
    />
    {/* Particle burst (growOut only) */}
    {type === 'growOut' && (
      <>
        {[0, 60, 120, 180, 240, 300].map((angle) => (
          <motion.div
            key={angle}
            style={{
              position: 'absolute',
              width: 4,
              height: 4,
              borderRadius: '50%',
              background: '#CE93D8',
              boxShadow: '0 0 6px rgba(206, 147, 216, 0.8)',
            }}
            initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
            animate={{
              x: Math.cos(angle * Math.PI / 180) * cellSize * 0.6,
              y: Math.sin(angle * Math.PI / 180) * cellSize * 0.6,
              opacity: 0,
              scale: 0.3,
            }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
          />
        ))}
      </>
    )}
  </motion.div>
));

const GameBoard: React.FC<GameBoardProps> = ({ gameState, onCellClick, hintPath, hintPathHazards, onMovementLockChange, onExitAnimationComplete, restartCounter }) => {
  if (!gameState) {
    return <div className="game-board">Loading...</div>;
  }

  const { level, position } = gameState;
  const { shopState } = useShop();

  // Get equipped character configuration and props
  const equippedCharacterId = shopState.equippedCharacter || 'default_hero';
  const characterConfig = CHARACTER_CONFIGS[equippedCharacterId] || {};
  
  // Convert equipped prop IDs to actual prop objects
  const equippedProps = useMemo(() => {
    return (shopState.equippedProps || [])
      .map(propId => PROPS_CATALOG.find(prop => prop.id === propId))
      .filter((prop): prop is NonNullable<typeof prop> => prop !== undefined); // Remove any undefined values
  }, [shopState.equippedProps]);
  // Compute vision range and phase status from equipped props
  const { visionRange, hasPhase } = useMemo(() => {
    const propManager = getPropEffectsManager();
    const passiveEffects = equippedProps.flatMap(p => p.effects || []);
    return {
      visionRange: propManager.getVisionRange(passiveEffects),
      hasPhase: propManager.canPhase(passiveEffects),
    };
  }, [equippedProps]);

  const [displayPosition, setDisplayPosition] = useState<Position>(position);
  const { cameraStyle, cameraPhase, completeEntrance, completeExit, isLocked } =
    useCameraZoom(gameState, restartCounter);

  // Teleport tile effect state - separate arrays for landing, suckIn, and growOut
  const [landingEffects, setLandingEffects] = useState<Array<{
    id: number;
    position: Position;
  }>>([]);
  const [suckInEffects, setSuckInEffects] = useState<Array<{
    id: number;
    position: Position;
  }>>([]);
  const [growOutEffects, setGrowOutEffects] = useState<Array<{
    id: number;
    position: Position;
  }>>([]);
  const teleportIdRef = useRef(0);
  const teleportingRef = useRef(false);
  const teleportTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Listen for teleport events to show tile effects and delay displayPosition
  useEffect(() => {
    const handleTeleport = (event: Event) => {
      const { from, to } = (event as CustomEvent).detail;
      const id = teleportIdRef.current++;

      // Hold character visually at source during landing + suckIn phases
      teleportingRef.current = true;
      setDisplayPosition([from[0], from[1]]);

      // Show landing glow immediately on the source tile
      setLandingEffects(prev => [...prev, { id, position: [from[0], from[1]] }]);

      // Show suckIn tile effect after landing phase (500ms)
      teleportTimersRef.current.push(setTimeout(() => {
        setLandingEffects(prev => prev.filter(e => e.id !== id));
        setSuckInEffects(prev => [...prev, { id, position: [from[0], from[1]] }]);
      }, 500));

      // After suckIn completes (800ms), snap to destination and show growOut
      teleportTimersRef.current.push(setTimeout(() => {
        teleportingRef.current = false;
        setDisplayPosition([to[0], to[1]]);
        setGrowOutEffects(prev => [...prev, { id, position: [to[0], to[1]] }]);
      }, 800));

      // Clean up suckIn effect
      teleportTimersRef.current.push(setTimeout(() => {
        setSuckInEffects(prev => prev.filter(e => e.id !== id));
      }, 1100));

      // Clean up growOut effect
      teleportTimersRef.current.push(setTimeout(() => {
        setGrowOutEffects(prev => prev.filter(e => e.id !== id));
      }, 1500));
    };

    window.addEventListener('teleport', handleTeleport);
    return () => {
      window.removeEventListener('teleport', handleTeleport);
      teleportTimersRef.current.forEach(clearTimeout);
      teleportTimersRef.current = [];
    };
  }, []);

  // Floating coin text state
  const [floatingTexts, setFloatingTexts] = useState<Array<{
    id: number;
    position: Position;
    value: number;
  }>>([]);
  const floatingTextIdRef = useRef(0);

  // Listen for coinCollected events
  useEffect(() => {
    const handleCoinCollected = (event: Event) => {
      const { position: pos, value } = (event as CustomEvent).detail;
      const id = floatingTextIdRef.current++;
      setFloatingTexts(prev => [...prev, { id, position: pos, value }]);
      setTimeout(() => {
        setFloatingTexts(prev => prev.filter(t => t.id !== id));
      }, 800);
    };

    window.addEventListener('coinCollected', handleCoinCollected);
    return () => window.removeEventListener('coinCollected', handleCoinCollected);
  }, []);

  // Notify parent about movement lock state
  useEffect(() => {
    if (onMovementLockChange) {
      onMovementLockChange(isLocked);
    }
  }, [isLocked, onMovementLockChange]);

  // Animation state management
  const [animationState, setAnimationState] = useState<PlayerAnimationState>({
    isMoving: false,
    direction: null,
    hasCollided: false,
    previousPosition: position,
  });

  const [characterState, setCharacterState] = useState<CharacterGameState>('entering');
  const [levelReady, setLevelReady] = useState(false);
  const entranceAnimationRef = useRef<NodeJS.Timeout | null>(null);

  const previousPositionRef = useRef<Position>(position);

  // Track window size for responsive sizing
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Parse puzzle data (supports both standard levels via puzzles[] and infinite mode via infiniteGrid)
  const { gameGrid, cellSize, boardDimensions, levelData } = useMemo(() => {
    // In infinite mode, use the grid stored in gameState instead of puzzles[level]
    const isInfinite = gameState.gameMode === 'endless' && gameState.infiniteGrid;
    const puzzle = isInfinite ? null : (puzzles[level] as PuzzleData);

    if (!puzzle && !isInfinite)
      return { gameGrid: [], cellSize: 60, boardDimensions: { rows: 0, cols: 0 }, levelData: [] };

    const puzzleGrid = isInfinite ? gameState.infiniteGrid! : getPuzzleGrid(puzzle!);
    const grid: GameCell[] = [];
    const rows = puzzleGrid.length;
    const cols = puzzleGrid[0]?.length || 0;

    // Calculate cell size to fit screen
    const viewportWidth = windowSize.width;
    const viewportHeight = windowSize.height;

    // Account for UI elements (header, controls, etc)
    const availableWidth = viewportWidth * 0.9;
    const availableHeight = viewportHeight * 0.65; // More conservative for mobile

    // Calculate cell size based on available space
    const cellWidth = Math.floor(availableWidth / cols);
    const cellHeight = Math.floor(availableHeight / rows);

    // Use the smaller dimension to ensure board fits
    const maxCellSize = window.innerWidth < 768 ? 50 : 60; // Smaller max on mobile
    const calculatedCellSize = Math.min(cellWidth, cellHeight, maxCellSize);

    // Process grid cells
    puzzleGrid.forEach((row, rowIndex) => {
      const cells = row.split('');
      cells.forEach((cell, cellIndex) => {
        const cellPosition: Position = [cellIndex, rowIndex];
        let cellType: GameCell['type'] = 'empty';
        let cellData: any = undefined;

        switch (cell.toLowerCase()) {
          case 'x':
            cellType = 'rock';
            break;
          case 'l':
            cellType = 'lava';
            break;
          case 's':
            cellType = 'stop';
            break;
          case 't':
            cellType = 'teleporter';
            break;
          case 'o':
            cellType = 'start';
            break;
          case 'e':
            cellType = 'end';
            break;
          case 'p':
            cellType = 'powerup';
            cellData = { type: 'coin' };
            break;
          default:
            cellType = 'empty';
            break;
        }

        // Pre-compute base z-index
        const baseZIndex = rowIndex * 100 + cellIndex;

        grid.push({
          type: cellType,
          position: cellPosition,
          data: cellData,
          zIndex: baseZIndex,
        });
      });
    });

    // Create level data for particle system
    const levelDataArray = puzzleGrid.map(row => row.split(''));

    return {
      gameGrid: grid,
      cellSize: calculatedCellSize,
      boardDimensions: { rows, cols },
      levelData: levelDataArray,
    };
  }, [level, windowSize, gameState.gameMode, gameState.infiniteGrid]);

  // Get current tile type for character elevation
  const currentTileType = useMemo(() => {
    const currentCell = gameGrid.find(
      cell => cell.position[0] === position[0] && cell.position[1] === position[1]
    );
    return currentCell?.type || 'empty';
  }, [gameGrid, position]);

  // Detect movement and collisions
  useEffect(() => {
    const prevPos = previousPositionRef.current;
    const hasMovedX = prevPos[0] !== position[0];
    const hasMovedY = prevPos[1] !== position[1];

    if (hasMovedX || hasMovedY) {
      let direction: 'up' | 'down' | 'left' | 'right' | null = null;
      if (hasMovedX) {
        direction = position[0] > prevPos[0] ? 'right' : 'left';
      } else if (hasMovedY) {
        direction = position[1] > prevPos[1] ? 'down' : 'up';
      }

      setAnimationState({
        isMoving: true,
        direction,
        hasCollided: false,
        previousPosition: prevPos,
      });

      // Update display position during normal gameplay (skip during teleport - handled by teleport listener)
      if (cameraPhase === 'playing' && !teleportingRef.current) {
        setDisplayPosition(position);
      }

      setTimeout(() => {
        setAnimationState(prev => ({ ...prev, isMoving: false }));
      }, 300);

      previousPositionRef.current = position;
    }
  }, [position]);

  // Handle entrance animation when camera zooms in
  useEffect(() => {
    if (cameraPhase === 'entrance' && levelReady) {
      // Find best entrance position
      const edgeTiles = gameGrid.filter(cell => {
        const isEdge =
          cell.position[0] === 0 ||
          cell.position[0] === boardDimensions.cols - 1 ||
          cell.position[1] === 0 ||
          cell.position[1] === boardDimensions.rows - 1;
        const isWalkable = cell.type === 'empty' || cell.type === 'start';
        return isEdge && isWalkable;
      });

      let bestEntrance = position;
      let minDistance = Infinity;

      edgeTiles.forEach(tile => {
        const distance =
          Math.abs(tile.position[0] - position[0]) + Math.abs(tile.position[1] - position[1]);
        if (distance < minDistance) {
          minDistance = distance;
          bestEntrance = tile.position;
        }
      });

      // Set entrance position off-screen
      let entrancePos: Position;
      if (bestEntrance[0] === 0) {
        entrancePos = [-2, bestEntrance[1]];
      } else if (bestEntrance[0] === boardDimensions.cols - 1) {
        entrancePos = [boardDimensions.cols + 1, bestEntrance[1]];
      } else if (bestEntrance[1] === 0) {
        entrancePos = [bestEntrance[0], -2];
      } else {
        entrancePos = [bestEntrance[0], boardDimensions.rows + 1];
      }

      setDisplayPosition(entrancePos);

      // Animate entrance after camera zoom
      setTimeout(() => {
        const steps = 10;
        let currentStep = 0;

        const animate = () => {
          if (currentStep <= steps) {
            const progress = currentStep / steps;
            const currentX = entrancePos[0] + (position[0] - entrancePos[0]) * progress;
            const currentY = entrancePos[1] + (position[1] - entrancePos[1]) * progress;

            setDisplayPosition([currentX, currentY]);

            // Update animation state
            const deltaX = position[0] - entrancePos[0];
            const deltaY = position[1] - entrancePos[1];
            setAnimationState(prev => ({
              ...prev,
              isMoving: true,
              direction:
                Math.abs(deltaX) > Math.abs(deltaY)
                  ? deltaX > 0
                    ? 'right'
                    : 'left'
                  : deltaY > 0
                  ? 'down'
                  : 'up',
            }));

            currentStep++;
            entranceAnimationRef.current = setTimeout(animate, 80);
          } else {
            // Entrance complete
            setDisplayPosition(position);
            setCharacterState('playing');
            setAnimationState(prev => ({ ...prev, isMoving: false }));
            completeEntrance();
          }
        };

        animate();
      }, 800); // Wait for camera zoom
    }

    return () => {
      if (entranceAnimationRef.current) {
        clearTimeout(entranceAnimationRef.current);
      }
    };
  }, [cameraPhase, levelReady, gameGrid, position, boardDimensions, completeEntrance]);

  // Handle exit animation - fly away
  const exitAnimationRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (cameraPhase === 'exit') {
      // Set character to won state first for victory celebration
      setCharacterState('won');

      // After brief celebration, trigger fly-away animation
      exitAnimationRef.current = setTimeout(() => {
        setCharacterState('exiting');
        // Character3D handles the fly-away animation internally
        // (spin -> launch upward -> gone)
        // Complete exit after the fly-away finishes
        exitAnimationRef.current = setTimeout(() => {
          setAnimationState(prev => ({ ...prev, isMoving: false }));
          completeExit();
        }, 2000);
      }, 1000);
    }

    return () => {
      if (exitAnimationRef.current) {
        clearTimeout(exitAnimationRef.current);
      }
    };
  }, [cameraPhase, completeExit]);
  
  // Trigger level complete modal when camera animations are fully complete
  useEffect(() => {
    if (cameraPhase === 'complete' && onExitAnimationComplete) {
      onExitAnimationComplete();
    }
  }, [cameraPhase, onExitAnimationComplete]);

  // Level transition - reset on level change OR restart
  useEffect(() => {
    setCharacterState('entering');
    setLevelReady(false);
    setDisplayPosition(gameState.position); // Reset display position
    setAnimationState({
      isMoving: false,
      direction: null,
      hasCollided: false,
      previousPosition: gameState.position,
    });
    
    // Reset position refs
    previousPositionRef.current = gameState.position;
    
    // Clear any pending entrance animation
    if (entranceAnimationRef.current) {
      clearTimeout(entranceAnimationRef.current);
      entranceAnimationRef.current = null;
    }

    const readyTimer = setTimeout(() => {
      setLevelReady(true);
    }, 200);

    return () => clearTimeout(readyTimer);
  }, [level, restartCounter, gameState.infiniteState?.wave]);

  const handleEntranceComplete = useCallback(() => {
    setCharacterState('playing');
  }, []);

  const lastTapLineRef = useRef(-1);
  const handleCharacterTap = useCallback(() => {
    let idx: number;
    do {
      idx = Math.floor(Math.random() * CHARACTER_TAP_LINES.length);
    } while (idx === lastTapLineRef.current && CHARACTER_TAP_LINES.length > 1);
    lastTapLineRef.current = idx;
    const commentary = getCommentarySystem();
    commentary.trigger(CHARACTER_TAP_LINES[idx], 'excited', 'high');
  }, []);

  // Listen for death events to trigger death animation
  useEffect(() => {
    const handleGameOver = () => {
      setCharacterState('lost');
    };
    const handleLifeLost = () => {
      setCharacterState('lost');
    };
    const handleResetAfterDeath = () => {
      // After death animation, re-enter
      setCharacterState('entering');
    };
    window.addEventListener('gameOver', handleGameOver);
    window.addEventListener('lifeLost', handleLifeLost);
    window.addEventListener('resetAfterDeath', handleResetAfterDeath);
    return () => {
      window.removeEventListener('gameOver', handleGameOver);
      window.removeEventListener('lifeLost', handleLifeLost);
      window.removeEventListener('resetAfterDeath', handleResetAfterDeath);
    };
  }, []);

  // Determine particle theme based on level content
  const getParticleTheme = useCallback(() => {
    const hasLava = gameState.rocks.some(rock => rock[2] === 'lava');
    const hasIce = gameState.rocks.some(rock => rock[2] === 'stop');
    const hasTeleporter = gameState.rocks.some(rock => rock[2] === 'teleporter');

    if (hasTeleporter) return 'magical';
    if (hasLava) return 'lava';
    if (hasIce) return 'ice';
    if (gameState.level > 30) return 'magical';
    if (gameState.level > 20) return 'cave';
    return 'forest';
  }, [gameState.rocks, gameState.level]);

  // Check if player reached the end (handled by camera system now)
  // useEffect(() => {
  //   const endCell = gameGrid.find(cell => cell.type === 'end');
  //   if (endCell && position[0] === endCell.position[0] && position[1] === endCell.position[1]) {
  //     setCharacterState('victory');
  //   }
  // }, [position, gameGrid]);

  // Compute exit approach directions for the exit arrow indicator
  const exitApproachDirs = useMemo(() => {
    if (!gameState.end) return [];
    const isInfinite = gameState.gameMode === 'endless' && gameState.infiniteGrid;
    const puzzle = isInfinite ? null : (puzzles[level] as PuzzleData);
    if (!puzzle && !isInfinite) return [];
    const puzzleGrid = isInfinite ? gameState.infiniteGrid! : getPuzzleGrid(puzzle!);
    return getExitApproachDirections(gameState.end, puzzleGrid, gameState.rocks);
  }, [gameState.end, gameState.rocks, level, gameState.gameMode, gameState.infiniteGrid]);

  // Calculate board container size
  const boardStyle = {
    position: 'relative' as const,
    width: boardDimensions.cols * cellSize,
    height: boardDimensions.rows * cellSize,
    margin: '0 auto',
    ['--cell' as any]: `${cellSize}px`,
  };
  const currentWorld = useMemo(() => getWorldForLevel(gameState.level), [gameState.level]);

  return (
    <div
      className={`game-board-wrapper game-board-wrapper--${cameraPhase} game-board-wrapper--${currentWorld.id}`}
      data-camera-phase={cameraPhase}
      data-world-id={currentWorld.id}
    >
      <div className="game-board-stage" style={cameraStyle}>
        <div
          className="game-board-container"
          style={boardStyle}
          data-cols={boardDimensions.cols}
          data-rows={boardDimensions.rows}
        >
          {/* Render all tiles with smart z-index calculation */}
          {gameGrid.map(cell => {
            const isPath = cell.type === 'empty' || cell.type === 'start' || cell.type === 'end';

            // Use pre-computed base z-index; rocks need dynamic adjustment relative to player
            let finalZIndex = cell.zIndex ?? (cell.position[1] * 100 + cell.position[0]);
            if (cell.type === 'rock') {
              const isInFront = cell.position[1] > position[1];
              const isAdjacent =
                Math.abs(cell.position[0] - position[0]) <= 1 &&
                Math.abs(cell.position[1] - position[1]) <= 1;
              if (isInFront || (isAdjacent && cell.position[1] >= position[1])) {
                finalZIndex += 2000;
              }
            }

            return (
              <Tile3D
                key={`tile-${cell.position[0]}-${cell.position[1]}`}
                type={cell.type}
                position={cell.position}
                cellSize={cellSize}
                isPath={isPath}
                zIndex={finalZIndex}
                exitApproachDirections={cell.type === 'end' ? exitApproachDirs : undefined}
              />
            );
          })}

          {/* Hint path: glow overlays, directional arrows, connecting lines, and hazard markers */}
          <AnimatePresence>
            {hintPath && hintPath.map((pos, index) => {
              const isHazard = hintPathHazards?.has(`${pos[0]},${pos[1]}`);
              const isLast = index === hintPath.length - 1;
              const nextPos = !isLast ? hintPath[index + 1] : null;

              // Compute arrow direction toward the next stop in the path
              let arrowRotation = 0; // degrees, 0 = right
              if (nextPos) {
                const dx = nextPos[0] - pos[0];
                const dy = nextPos[1] - pos[1];
                if (dx > 0) arrowRotation = 0;
                else if (dx < 0) arrowRotation = 180;
                else if (dy > 0) arrowRotation = 90;
                else if (dy < 0) arrowRotation = -90;
              }

              // Connecting line from this tile center to next tile center
              let lineLength = 0;
              let lineAngle = 0;
              if (nextPos) {
                const dx = (nextPos[0] - pos[0]) * cellSize;
                const dy = (nextPos[1] - pos[1]) * cellSize;
                lineLength = Math.sqrt(dx * dx + dy * dy);
                lineAngle = Math.atan2(dy, dx) * (180 / Math.PI);
              }

              const tileColor = isHazard
                ? 'rgba(255, 100, 50, 0.3)'
                : 'rgba(0, 0, 0, 0.35)';
              const glowColor = isHazard
                ? '0 0 16px rgba(255, 100, 50, 0.7), inset 0 0 8px rgba(255, 100, 50, 0.3)'
                : '0 0 12px rgba(0, 0, 0, 0.5), inset 0 0 6px rgba(255, 255, 255, 0.08)';
              const borderColor = isHazard
                ? 'rgba(255, 100, 50, 0.6)'
                : 'rgba(255, 255, 255, 0.25)';

              return (
                <React.Fragment key={`hint-group-${pos[0]}-${pos[1]}`}>
                  {/* Tile glow overlay */}
                  <motion.div
                    key={`hint-${pos[0]}-${pos[1]}`}
                    style={{
                      position: 'absolute',
                      left: pos[0] * cellSize,
                      top: pos[1] * cellSize,
                      width: cellSize,
                      height: cellSize,
                      background: tileColor,
                      boxShadow: glowColor,
                      border: `1px solid ${borderColor}`,
                      borderRadius: 4,
                      zIndex: pos[1] * 100 + pos[0] + 500,
                      pointerEvents: 'none',
                    }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{
                      opacity: [0.5, 0.9, 0.5],
                      scale: 1,
                    }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      opacity: { duration: 1.5, repeat: Infinity, delay: index * 0.1 },
                      scale: { duration: 0.3 },
                    }}
                  />

                  {/* Directional arrow pointing to next tile */}
                  {nextPos && (
                    <motion.div
                      key={`hint-arrow-${pos[0]}-${pos[1]}`}
                      style={{
                        position: 'absolute',
                        left: pos[0] * cellSize + cellSize / 2 - cellSize * 0.15,
                        top: pos[1] * cellSize + cellSize / 2 - cellSize * 0.15,
                        width: cellSize * 0.3,
                        height: cellSize * 0.3,
                        zIndex: pos[1] * 100 + pos[0] + 510,
                        pointerEvents: 'none',
                        transform: `rotate(${arrowRotation}deg)`,
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.6, 1.0, 0.6] }}
                      exit={{ opacity: 0 }}
                      transition={{
                        opacity: { duration: 1.5, repeat: Infinity, delay: index * 0.1 },
                      }}
                    >
                      {/* CSS triangle arrow pointing right (rotated via parent) */}
                      <div
                        style={{
                          width: 0,
                          height: 0,
                          borderTop: `${cellSize * 0.15}px solid transparent`,
                          borderBottom: `${cellSize * 0.15}px solid transparent`,
                          borderLeft: `${cellSize * 0.25}px solid ${isHazard ? 'rgba(255, 180, 100, 0.9)' : 'rgba(255, 255, 255, 0.7)'}`,
                        }}
                      />
                    </motion.div>
                  )}

                  {/* Dashed connecting line to next tile */}
                  {nextPos && lineLength > 0 && (
                    <motion.div
                      key={`hint-line-${pos[0]}-${pos[1]}`}
                      style={{
                        position: 'absolute',
                        left: pos[0] * cellSize + cellSize / 2,
                        top: pos[1] * cellSize + cellSize / 2,
                        width: lineLength,
                        height: 2,
                        transformOrigin: '0 50%',
                        transform: `rotate(${lineAngle}deg)`,
                        borderTop: `2px dashed ${isHazard ? 'rgba(255, 100, 50, 0.5)' : 'rgba(255, 255, 255, 0.3)'}`,
                        zIndex: pos[1] * 100 + pos[0] + 499,
                        pointerEvents: 'none',
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.4, 0.8, 0.4] }}
                      exit={{ opacity: 0 }}
                      transition={{
                        opacity: { duration: 1.5, repeat: Infinity, delay: index * 0.1 },
                      }}
                    />
                  )}

                  {/* Hazard warning icon for tiles near enemies */}
                  {isHazard && (
                    <motion.div
                      key={`hint-hazard-${pos[0]}-${pos[1]}`}
                      style={{
                        position: 'absolute',
                        left: pos[0] * cellSize + cellSize * 0.6,
                        top: pos[1] * cellSize + 1,
                        width: cellSize * 0.35,
                        height: cellSize * 0.35,
                        zIndex: pos[1] * 100 + pos[0] + 520,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: Math.max(8, cellSize * 0.28),
                        fontWeight: 800,
                        color: '#fff',
                        background: 'rgba(220, 50, 20, 0.85)',
                        borderRadius: '50%',
                        lineHeight: 1,
                        textShadow: '0 0 3px rgba(0,0,0,0.6)',
                      }}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{
                        opacity: [0.8, 1, 0.8],
                        scale: [1, 1.15, 1],
                      }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{
                        opacity: { duration: 1.0, repeat: Infinity },
                        scale: { duration: 1.0, repeat: Infinity },
                      }}
                    >
                      !
                    </motion.div>
                  )}
                </React.Fragment>
              );
            })}
          </AnimatePresence>

          {/* Warp selection overlays */}
          <AnimatePresence>
            {gameState.isWarpSelecting && (
              <>
                {/* "Select destination" banner */}
                <motion.div
                  key="warp-banner"
                  style={{
                    position: 'absolute',
                    top: -36,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 3000,
                    background: 'rgba(156, 39, 176, 0.85)',
                    color: '#fff',
                    padding: '6px 16px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  Select destination
                </motion.div>

                {/* Clickable tile overlays for valid warp destinations */}
                {gameGrid
                  .filter(cell => cell.type !== 'rock' && cell.type !== 'lava')
                  .map(cell => (
                    <motion.div
                      key={`warp-${cell.position[0]}-${cell.position[1]}`}
                      style={{
                        position: 'absolute',
                        left: cell.position[0] * cellSize,
                        top: cell.position[1] * cellSize,
                        width: cellSize,
                        height: cellSize,
                        background: 'rgba(156, 39, 176, 0.2)',
                        boxShadow: '0 0 12px rgba(156, 39, 176, 0.6)',
                        borderRadius: 4,
                        zIndex: cell.position[1] * 100 + cell.position[0] + 2500,
                        cursor: 'pointer',
                      }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.4, 0.8, 0.4] }}
                      exit={{ opacity: 0 }}
                      transition={{ opacity: { duration: 1.5, repeat: Infinity } }}
                      onClick={() => onCellClick?.(cell.position)}
                    />
                  ))}
              </>
            )}
          </AnimatePresence>

          {/* Vision range highlight overlays */}
          {visionRange > 0 && gameGrid
            .filter(cell => {
              const dist = Math.abs(cell.position[0] - position[0]) + Math.abs(cell.position[1] - position[1]);
              return dist > 1 && dist <= visionRange;
            })
            .map(cell => (
              <div
                key={`vision-${cell.position[0]}-${cell.position[1]}`}
                style={{
                  position: 'absolute',
                  left: cell.position[0] * cellSize,
                  top: cell.position[1] * cellSize,
                  width: cellSize,
                  height: cellSize,
                  background: 'rgba(100, 149, 237, 0.1)',
                  boxShadow: 'inset 0 0 8px rgba(100, 149, 237, 0.3)',
                  borderRadius: 2,
                  zIndex: cell.position[1] * 100 + cell.position[0] + 50,
                  pointerEvents: 'none',
                }}
              />
            ))
          }

          {/* Teleport landing glow on source tile */}
          {landingEffects.map(effect => (
            <motion.div
              key={`land-${effect.id}`}
              style={{
                position: 'absolute',
                left: effect.position[0] * cellSize,
                top: effect.position[1] * cellSize,
                width: cellSize,
                height: cellSize,
                zIndex: 2500,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Pulsing portal glow building up */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: '70%',
                  height: '70%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(156, 39, 176, 0.6) 0%, rgba(156, 39, 176, 0.2) 50%, transparent 70%)',
                  boxShadow: '0 0 20px rgba(156, 39, 176, 0.5)',
                }}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.3, 0.8, 0.3] }}
                transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* Rotating ring building anticipation */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: '80%',
                  height: '80%',
                  borderRadius: '50%',
                  border: '2px dashed rgba(206, 147, 216, 0.5)',
                }}
                initial={{ rotate: 0, opacity: 0 }}
                animate={{ rotate: 360, opacity: 0.7 }}
                transition={{ rotate: { duration: 0.5, ease: 'linear' }, opacity: { duration: 0.2 } }}
              />
            </motion.div>
          ))}
          {/* Teleport suckIn effects on source tiles */}
          {suckInEffects.map(effect => (
            <TeleportEffect key={`suck-${effect.id}`} position={effect.position} cellSize={cellSize} type="suckIn" />
          ))}
          {/* Teleport growOut effects on destination tiles */}
          {growOutEffects.map(effect => (
            <TeleportEffect key={`grow-${effect.id}`} position={effect.position} cellSize={cellSize} type="growOut" />
          ))}

          {/* Render enemies (before character so they appear behind player) */}
          {gameState.enemies.map((enemy, idx) => (
            <EnemyRenderer
              key={enemy.id}
              enemy={enemy}
              cellSize={cellSize}
              isFrozen={gameState.frozenTurns > 0}
              playerPosition={gameState.position}
              entranceDelay={idx * 0.1}
            />
          ))}

          {/* Character with dynamic z-index based on position */}
          {levelReady && (
            <div
              style={{
                position: 'absolute',
                zIndex: position[1] * 100 + position[0] + 1000, // Character layer offset of 1000
              }}
            >
              <Character
                position={[Math.round(displayPosition[0]), Math.round(displayPosition[1])]}
                direction={animationState.direction}
                isMoving={animationState.isMoving}
                cellSize={cellSize}
                hasCollided={gameState.hasCollided || false}
                gameState={characterState}
                onEntranceComplete={handleEntranceComplete}
                onTap={handleCharacterTap}
                style="3d"
                currentTileType={currentTileType}
                config={characterConfig}
                equippedProps={equippedProps}
              />
            </div>
          )}

          {/* Shield visual overlay on character */}
          <AnimatePresence>
            {shopState.powerups.shield > 0 && levelReady && (
              <motion.div
                key="shield-overlay"
                style={{
                  position: 'absolute',
                  left: displayPosition[0] * cellSize + cellSize / 2 - (cellSize * 1.4) / 2,
                  top: displayPosition[1] * cellSize + cellSize / 2 - (cellSize * 1.4) / 2,
                  width: cellSize * 1.4,
                  height: cellSize * 1.4,
                  borderRadius: '50%',
                  border: '2px solid rgba(0, 200, 255, 0.6)',
                  boxShadow: '0 0 15px rgba(0, 200, 255, 0.4)',
                  zIndex: position[1] * 100 + position[0] + 1001,
                  pointerEvents: 'none',
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                  opacity: [0.6, 0.9, 0.6],
                  scale: [1.0, 1.05, 1.0],
                }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{
                  opacity: { duration: 2, repeat: Infinity },
                  scale: { duration: 2, repeat: Infinity },
                }}
              />
            )}
          </AnimatePresence>

          {/* Phase (ghost) visual overlay on character */}
          <AnimatePresence>
            {hasPhase && levelReady && (
              <motion.div
                key="phase-overlay"
                style={{
                  position: 'absolute',
                  left: displayPosition[0] * cellSize,
                  top: displayPosition[1] * cellSize,
                  width: cellSize,
                  height: cellSize,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(156, 39, 176, 0.15) 0%, transparent 70%)',
                  zIndex: position[1] * 100 + position[0] + 1002,
                  pointerEvents: 'none',
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </AnimatePresence>

          {/* Invincibility visual overlay on character */}
          <AnimatePresence>
            {shopState.powerups.invincibility > 0 && levelReady && (
              <motion.div
                key="invincibility-overlay"
                style={{
                  position: 'absolute',
                  left: displayPosition[0] * cellSize + cellSize / 2 - (cellSize * 1.6) / 2,
                  top: displayPosition[1] * cellSize + cellSize / 2 - (cellSize * 1.6) / 2,
                  width: cellSize * 1.6,
                  height: cellSize * 1.6,
                  borderRadius: '50%',
                  boxShadow: '0 0 20px rgba(255, 215, 0, 0.6)',
                  background: 'radial-gradient(circle, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
                  zIndex: position[1] * 100 + position[0] + 1001,
                  pointerEvents: 'none',
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                  opacity: [0.5, 0.8, 0.5],
                  scale: [1.0, 1.1, 1.0],
                }}
                exit={{ opacity: 0, scale: 0.5 }}
                transition={{
                  opacity: { duration: 1.5, repeat: Infinity },
                  scale: { duration: 1.5, repeat: Infinity },
                }}
              />
            )}
          </AnimatePresence>

          {/* Render all collectibles - let Coin component handle collection state */}
          {gameState.collectibles.map(collectible => (
            <Coin
              key={collectible.id}
              position={collectible.position}
              cellSize={cellSize}
              type={collectible.type}
              isCollected={collectible.isCollected}
            />
          ))}

          {/* Floating coin text at collection point */}
          <AnimatePresence>
            {floatingTexts.map(ft => (
              <motion.div
                key={`float-${ft.id}`}
                style={{
                  position: 'absolute',
                  left: ft.position[0] * cellSize,
                  top: ft.position[1] * cellSize,
                  width: cellSize,
                  height: cellSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2500,
                  pointerEvents: 'none',
                  color: '#f1c40f',
                  fontSize: 16,
                  fontWeight: 'bold',
                  textShadow: '0 0 4px rgba(0,0,0,0.5)',
                }}
                initial={{ opacity: 1, y: 0, scale: 0.8 }}
                animate={{ opacity: 0, y: -cellSize, scale: 1.2 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                +{ft.value}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Procedural Particle System - lazy loaded */}
          <Suspense fallback={null}>
            <ProceduralParticleSystem
              levelData={levelData}
              cellSize={cellSize}
              characterPosition={position}
              isEnabled={gameState.gameSettings.effects}
              particleDensity={0.3}
              theme={getParticleTheme()}
              graphicsQuality={gameState.gameSettings.graphics}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default memo(GameBoard);
